<!--suppress XmlDuplicatedId -->
<template>
  <div>
    <div v-show="$refs.upload && $refs.upload.dropActive" class="drop-active">
      <h3>{{ $t("mfu.dragNdrop") }}</h3>
    </div>
    <div class="upload">
      <div class="table-responsive">
        <table class="table table-hover">
          <caption>
            {{
              $t("mfu.attachmentList")
            }}
          </caption>
          <thead>
            <tr>
              <th>#</th>
              <th>{{ $t("mfu.preview") }}</th>
              <th>{{ $t("mfu.name") }}</th>
              <th>{{ $t("mfu.size") }}</th>
              <th>{{ $t("mfu.action") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(file, index) in files" :key="file.id">
              <td>{{ index + 1 }}</td>
              <td>
                <img
                  v-if="file.thumb"
                  alt="preview do arquivo"
                  :src="file.thumb"
                  width="40"
                  height="auto"
                />
                <span v-else>{{ $t("mfu.noImage") }}</span>
              </td>
              <td>
                <div class="filename" :class="{ 'text-danger': file.error }">
                  {{ file.name }}<br />
                  <small v-if="file.error">{{
                    getErrorDescription(file.error)
                  }}</small>
                </div>
                <div
                  v-if="file.active || file.progress !== '0.00'"
                  class="progress"
                >
                  <div
                    :class="{
                      'progress-bar': true,
                      'progress-bar-striped': true,
                      'bg-danger': file.error,
                      'progress-bar-animated': file.active,
                    }"
                    role="progressbar"
                    :style="{ width: file.progress + '%' }"
                  >
                    {{ file.progress }}%
                  </div>
                </div>
              </td>
              <td>{{ file.size | formatSize }}</td>
              <td>
                <b-button
                  :disabled="disabled"
                  variant="link"
                  @click="
                    file.active || file.success ? false : onEditFileShow(file)
                  "
                  ><i class="fas fa-edit" aria-hidden="true"></i>
                  {{ $t("mfu.edit") }}
                </b-button>
                <b-button
                  :disabled="disabled"
                  variant="link"
                  @click="$refs.upload.remove(file)"
                  ><i class="fas fa-trash" aria-hidden="true"></i>
                  {{ $t("mfu.remove") }}
                </b-button>
              </td>
            </tr>
            <tr v-if="!disabled">
              <td colspan="7">
                <div class="text-center" :class="{ 'p-5': files.length === 0 }">
                  <h4>{{ $t("mfu.dragNdropHere") }}<br />{{ $t("mfu.or") }}</h4>
                  <b-button variant="primary" size="lg" @click="selectFiles">{{
                    $t("mfu.selectFiles")
                  }}</b-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <file-upload
        ref="upload"
        v-model="files"
        :post-action="postAction"
        :custom-action="handleUpload"
        :accept="accept"
        :multiple="true"
        :directory="false"
        :size="size || 0"
        :thread="thread < 1 ? 1 : thread > 5 ? 5 : thread"
        :headers="headers"
        :data="data"
        :drop="true"
        :drop-directory="true"
        :add-index="false"
        @input-filter="inputFilter"
        @input-file="inputFile"
      >
      </file-upload>
    </div>

    <b-modal
      id="modal-edit-file"
      v-model="editFile.show"
      :title="$t('mfu.editFile')"
    >
      <b-form no-validate>
        <b-form-group :label="$t('mfu.name')">
          <b-form-input
            id="name"
            v-model="editFile.name"
            type="text"
            class="form-control"
            required
            :placeholder="$t('mfu.filenameField')"
          >
          </b-form-input>
        </b-form-group>
        <b-form-group
          v-if="
            editFile.show &&
            editFile.blob &&
            editFile.type &&
            editFile.type.substr(0, 6) === 'image/'
          "
        >
          <label>{{ $t("mfu.image") }}: </label>
          <div class="edit-image">
            <img
              ref="editImage"
              :src="editFile.blob"
              :alt="$t('mfu.editImage')"
            />
          </div>
          <div class="edit-image-tool">
            <b-button-group>
              <b-button
                variant="primary"
                :title="$t('mfu.zoomIn')"
                @click="editFile.cropper.zoom(0.1)"
                ><i class="fas fa-search-plus" aria-hidden="true"></i
              ></b-button>
              <b-button
                variant="primary"
                :title="$t('mfu.zoomOut')"
                @click="editFile.cropper.zoom(-0.1)"
                ><i class="fas fa-search-minus" aria-hidden="true"></i
              ></b-button>
            </b-button-group>
            <b-button-group class="ml-1">
              <b-button
                variant="primary"
                :title="`${$t('mfu.rotate')} -90°`"
                @click="editFile.cropper.rotate(-90)"
                ><i class="fas fa-undo" aria-hidden="true"></i
              ></b-button>
              <b-button
                variant="primary"
                :title="`${$t('mfu.rotate')} 90°`"
                @click="editFile.cropper.rotate(90)"
                ><i class="fas fa-redo" aria-hidden="true"></i
              ></b-button>
            </b-button-group>
            <b-button-group class="ml-1">
              <b-button
                variant="primary"
                :title="$t('mfu.flipH')"
                @click="
                  editFile.cropper.scaleX(
                    (editFile.scaleX = editFile.scaleX * -1)
                  )
                "
                ><i class="fas fa-arrows-alt-h" aria-hidden="true"></i
              ></b-button>
              <b-button
                variant="primary"
                :title="$t('mfu.flipV')"
                @click="
                  editFile.cropper.scaleY(
                    (editFile.scaleY = editFile.scaleY * -1)
                  )
                "
                ><i class="fas fa-arrows-alt-v" aria-hidden="true"></i
              ></b-button>
            </b-button-group>
            <b-button-group class="ml-1">
              <b-button
                variant="primary"
                :title="$t('mfu.reset')"
                @click="editFile.cropper.reset()"
                ><i class="fas fa-sync-alt" aria-hidden="true"></i
              ></b-button>
              <b-button
                variant="primary"
                :title="$t('mfu.apply')"
                @click="editFile.cropper.crop()"
                ><i class="fas fa-check" aria-hidden="true"></i
              ></b-button>
              <b-button
                variant="primary"
                :title="$t('mfu.cancel')"
                @click="editFile.cropper.clear()"
                ><i class="fas fa-times" aria-hidden="true"></i
              ></b-button>
            </b-button-group>
          </div>
        </b-form-group>
      </b-form>
      <template slot="modal-footer">
        <b-button @click="editFile.show = false">{{
          $t("mfu.cancel")
        }}</b-button>
        <b-button variant="primary" @click="onEditorFile">{{
          $t("mfu.save")
        }}</b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.min.css";
import FileUpload from "vue-upload-component";

export default {
  name: "MultipleFileUpload",
  components: {
    FileUpload,
  },
  props: {
    postAction: {
      type: String,
      default: null,
    },
    headers: {
      type: Object,
      default: () => {
        return {};
      },
    },
    data: {
      type: Object,
      default: null,
    },
    disabled: Boolean,
  },
  data() {
    return {
      files: [],
      accept: "image/*,audio/*,video/*,text/plain,application/pdf",
      minSize: 1024,
      size: 1024 * 1024 * 10,
      thread: 3,
      name: "file",
      editFile: {
        show: false,
        name: "",
        scaleX: 1,
        scaleY: 1,
      },
      uploading: false,
      filesToUpload: 0,
    };
  },
  computed: {
    hasInvalidFiles() {
      for (const file of this.files) {
        if (file.error) {
          return true;
        }
      }
      return false;
    },
  },
  watch: {
    "editFile.show"(newValue, oldValue) {
      //  error
      if (!newValue && oldValue) {
        this.$refs.upload.update(this.editFile.id, {
          error: this.editFile.error || "",
        });
      }
      if (newValue) {
        this.$nextTick(function () {
          if (!this.$refs.editImage) {
            return;
          }
          let cropper = new Cropper(this.$refs.editImage, {
            autoCrop: false,
          });
          this.editFile = {
            scaleX: 1,
            scaleY: 1,
            ...this.editFile,
            cropper,
          };
        });
      }
    },
  },
  methods: {
    getErrorDescription(error) {
      switch (error) {
        case "size":
        case "extension":
        case "timeout":
        case "abort":
        case "network":
        case "server":
        case "denied":
        case "edit":
          return this.$i18n.t(`mfu.errors.${error}`);
        default:
          return `${this.$i18n.t("mfu.errors.unknown")} ${error}`;
      }
    },

    async handleUpload(file, component) {
      await component.uploadHtml5(file);
      this.filesToUpload--;
      if (this.filesToUpload === 0) {
        this.uploading = false;
        this.$emit("upload-complete");
      }
    },
    selectedFiles() {
      return this.files;
    },

    start() {
      if (this.uploading) return;
      this.uploading = true;
      let count = 0;
      for (const file of this.files) {
        if (!file.error) {
          count++;
        }
      }
      this.filesToUpload = count;
      this.$refs.upload.active = true;
    },
    stop() {
      this.uploading = false;
      this.$refs.upload.active = false;
    },
    selectFiles() {
      document.getElementById("file").click();
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Before adding a file
        // Filter system files or hide files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent();
        }
        // Filter php html js file
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent();
        }
      }
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        // Create a blob field
        newFile.blob = "";
        let URL = window.URL || window.webkitURL;
        if (URL && URL.createObjectURL) {
          newFile.blob = URL.createObjectURL(newFile.file);
        }
        // Thumbnails
        newFile.thumb = "";
        if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
          newFile.thumb = newFile.blob;
        }
        // size verification
        if (
          newFile.size > -1 &&
          this.minSize > 0 &&
          newFile.size < this.minSize
        ) {
          newFile.error = "size";
        }

        if (newFile.size > -1 && newFile.size > this.size) {
          newFile.error = "size";
        }
      }
    },

    // add, update, remove File Event
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        // update
        if (newFile.active && !oldFile.active) {
          // beforeSend
          // min size
          if (
            newFile.size > -1 &&
            this.minSize > 0 &&
            newFile.size < this.minSize
          ) {
            this.$refs.upload.update(newFile, { error: "size" });
          }
        }
        if (newFile.progress !== oldFile.progress) {
          // progress
        }
        if (newFile.error && !oldFile.error) {
          // error
        }
        if (newFile.success && !oldFile.success) {
          // success
        }
      }
      if (!newFile && oldFile) {
        // remove
        if (oldFile.success && oldFile.response.id) {
          // $.ajax({
          //   type: 'DELETE',
          //   url: '/upload/delete?id=' + oldFile.response.id,
          // })
        }
      }
    },
    alert(message) {
      alert(message);
    },
    onEditFileShow(file) {
      this.editFile = { ...file, show: true };
      this.$refs.upload.update(file, { error: "edit" });
    },
    onEditorFile() {
      if (!this.$refs.upload.features.html5) {
        this.alert(this.$i18n.t("mfu.errors.unsupported"));
        this.editFile.show = false;
        return;
      }
      let data = {
        name: this.editFile.name,
      };
      if (this.editFile.cropper) {
        let binStr = window.atob(
          this.editFile.cropper
            .getCroppedCanvas()
            .toDataURL(this.editFile.type)
            .split(",")[1]
        );
        let arr = new Uint8Array(binStr.length);
        for (let i = 0; i < binStr.length; i++) {
          arr[i] = binStr.charCodeAt(i);
        }
        data.file = new File([arr], data.name, { type: this.editFile.type });
        data.size = data.file.size;
      }
      this.$refs.upload.update(this.editFile.id, data);
      this.editFile.error = "";
      this.editFile.show = false;
    },
  },
};
</script>
<style scoped>
.edit-image img {
  max-width: 100%;
}

.edit-image-tool {
  margin-top: 0.6rem;
}
</style>
